import ElakayImage from '../../../components/images/ElakayImage';
import React, { useContext, useState } from "react";
import { MdAdd, MdImage, MdShoppingBasket, MdSync } from "react-icons/md";
import { toImageUrl } from "../../../config";
import { elakayAutoText } from "../../pageInside/dataElakay";
import { DropshippingLanguageContext, dropshippingText, useDropshippingText } from "../i18n";
import ResyncModal from "./ResyncModal";
import GlobalRatingBadge from "../components/GlobalRatingBadge";

const money = (value) => value !== null && value !== "" && Number.isFinite(Number(value))
  ? Number(value).toLocaleString(undefined, { style: "currency", currency: "USD" }) : "—";
const stock = (value) => value !== null && value !== "" && Number.isFinite(Number(value)) ? Number(value).toLocaleString() : "—";
const description = (value) => typeof value === "string" ? value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim() : "";

const ProductImage = ({ product }) => {
  const [failed, setFailed] = useState(false);
  return product.images?.[0] && !failed
    ? <ElakayImage preset="product-card" src={toImageUrl(product.images[0])} alt={product.name || ""} loading="lazy" onError={() => setFailed(true)} />
    : <span className="spm-product__placeholder" aria-hidden="true"><MdImage /></span>;
};

// Both views use the same navigation and wholesale quote actions. Source
// products remain read-only here; Add opens the existing Seller setup page.
// Button order/behavior: 1st = Add Product / Resynchronize (status-dependent), 2nd = wholesale,
// functionally UNCHANGED. Viewing the product is done via its image/name, not a separate icon button.
// `shop` (this provider's shop summary) and `onSynced` (bump the parent matrix's reload version) are
// passed down from ProviderMarketplaceBrowser, one render of this component per provider section.
export default function ProviderMarketplaceProducts({ products, view, linkedProductIds, onViewProduct, onBuyWholesale, onAddProduct, shop, onSynced }) {
  const translated = useDropshippingText();
  const fallbacks = {
    viewProduct: "View Product", providerPrice: "Provider price", added: "Added ✓",
    addProduct: "Add Product", wholesaleOnly: "Wholesale only", resynchronize: "Resynchronize",
    addNamed: "Add {name} to my shop", resyncNamed: "Resynchronize {name}",
    minimumOrder: "Min. order: {count}", stockAvailable: "{count} available",
  };
  const t = (key, values) => {
    const label = translated(key, values);
    return label === key ? elakayAutoText(`h_spm_${key}`, fallbacks[key] || key, values) : label;
  };
  const infoSource = useContext(DropshippingLanguageContext);
  const tw = dropshippingText(infoSource, "providerProductWizard");
  const ct = dropshippingText(infoSource, "dropshippingCommercial");
  const [resyncTarget, setResyncTarget] = useState(null);
  // sellerLink (this exact list's own attachSellerLinks -- active AND paused) is the authoritative
  // signal for what the resync modal needs; linkedProductIds (a separate, lighter "active only"
  // lookup used elsewhere) is an additional, non-authoritative hint so a card never flashes "Add"
  // for a product this Seller already linked just because that second fetch hasn't resolved yet.
  const isAdded = (product) => linkedProductIds?.has(String(product._id)) || Boolean(product.sellerLink && product.sellerLink.status !== "removed");
  const labels = {
    image: elakayAutoText("h_spm_image", "Image"),
    product: elakayAutoText("h_spm_product", "Product"),
    description: elakayAutoText("h_spm_description", "Description"),
    stock: elakayAutoText("h_spm_stock", "Stock"),
    actions: elakayAutoText("h_spm_actions", "Actions"),
  };
  const openResync = (product) => {
    if (!product.sellerLink) { onViewProduct(product); return; } // defensive fallback, see comment above
    setResyncTarget({
      _id: product.sellerLink._id,
      sellerProductId: product.sellerLink.sellerProductId,
      fulfillmentMode: product.sellerLink.fulfillmentMode,
      providerProductId: { _id: product._id, name: product.name, shop },
    });
  };
  const actions = (product) => {
    const added = isAdded(product);
    const wholesaleOnly = product.dropshippingEnabled === false;
    return <div className="spm-product__actions">
      <button type="button" className={`spm-circle ${added ? "spm-circle--added" : "spm-circle--add"}`}
        disabled={!added && wholesaleOnly}
        aria-label={t(added ? "resyncNamed" : "addNamed", { name: product.name })}
        title={t(added ? "resynchronize" : wholesaleOnly ? "wholesaleOnly" : "addProduct")}
        onClick={() => added ? openResync(product) : onAddProduct(product)}>
        {added ? <MdSync aria-hidden="true" /> : <MdAdd aria-hidden="true" />}
        <span className="spm-sr-only">{t(added ? "resynchronize" : wholesaleOnly ? "wholesaleOnly" : "addProduct")}</span>
      </button>
      {product.wholesaleEnabled && <button type="button" className="spm-circle spm-circle--wholesale" title={tw("t175")} onClick={() => onBuyWholesale(product)}>
        <MdShoppingBasket aria-hidden="true" /><span className="spm-sr-only">{tw("t175")}</span>
      </button>}
    </div>;
  };
  const name = (product) => <button type="button" className="spm-product__name" onClick={() => onViewProduct(product)}>{product.name}</button>;
  const moq = (product) => product.minimumOrderQuantity > 1 && <span className="spm-product__moq">{t("minimumOrder", { count: product.minimumOrderQuantity })}</span>;

  return <>
    {view === "list" ? <div className="spm-table-wrap">
      <table className="spm-table">
        <caption className="spm-sr-only">{elakayAutoText("h_spm_provider_products", "Provider products")}</caption>
        <thead><tr>{[labels.image, labels.product, labels.description, t("providerPrice"), labels.stock, labels.actions].map((label) => <th scope="col" key={label}>{label}</th>)}</tr></thead>
        <tbody>{products.map((product) => <tr key={product._id} className={isAdded(product) ? "spm-product--added" : ""}>
          <td className="spm-table__image"><button type="button" onClick={() => onViewProduct(product)} aria-label={`${t("viewProduct")}: ${product.name}`}><ProductImage product={product} /></button></td>
          <td className="spm-table__name" data-label={labels.product}>{name(product)}<GlobalRatingBadge ratings={product.ratings} size="12px" />{moq(product)}</td>
          <td className="spm-table__description" data-label={labels.description}><p>{description(product.description) || "—"}</p></td>
          <td data-label={t("providerPrice")}><strong className="spm-product__price">{money(product.basePrice)}</strong></td>
          <td data-label={labels.stock}>{stock(product.stock)}</td>
          <td className="spm-table__actions" data-label={labels.actions}>{actions(product)}</td>
        </tr>)}</tbody>
      </table>
    </div> : <div className="spm-product-grid">
      {products.map((product) => <article key={product._id} className={`spm-product${isAdded(product) ? " spm-product--added" : ""}`}>
        <button type="button" className="spm-product__image" onClick={() => onViewProduct(product)} aria-label={`${t("viewProduct")}: ${product.name}`}><ProductImage product={product} /></button>
        <div className="spm-product__body">
          <h3>{name(product)}</h3>
          <GlobalRatingBadge ratings={product.ratings} size="12px" />
          <span className="spm-product__price-label">{t("providerPrice")}</span>
          <strong className="spm-product__price">{money(product.basePrice)}</strong>
          <div className="spm-product__details">{moq(product)}{product.stock != null && Number.isFinite(Number(product.stock)) && <span>{t("stockAvailable", { count: product.stock })}</span>}</div>
          {actions(product)}
        </div>
      </article>)}
    </div>}

    <ResyncModal
      show={Boolean(resyncTarget)}
      onHide={() => setResyncTarget(null)}
      link={resyncTarget}
      ct={ct}
      onSynced={() => onSynced?.()}
    />
  </>;
}
